export const tagline = 'Build Together. Grow Forever.'

export const brand = {
  name: 'NODEMOS',
  descriptor: 'Software Product Design',
  location: 'Austria · EU data residency · Production systems',
}

export const philosophy =
  'Every technological leap demands a corresponding leap in accessibility. Interfaces are how that leap is achieved — and what gets left behind when it isn’t.'

export const home = {
  hero: {
    headline: 'Engineering partners for',
    headlineAccent: 'developers and technical teams',
    lead:
      'We design and ship cloud applications that are modular, standards-aware, and built to last — working alongside your engineers from architecture through production.',
    primaryCta: { label: 'Configure your product', to: '/onboarding', icon: 'fa-solid fa-sitemap' },
    pitchCta: { label: 'Pitch your idea', to: '/pitch', icon: 'fa-solid fa-rocket' },
    secondaryCta: { label: 'Contact us', to: '/contact', icon: 'fa-solid fa-envelope' },
  },
  trust: [
    { icon: 'fa-solid fa-scale-balanced', label: 'EU regulatory compliant' },
    { icon: 'fa-solid fa-magnifying-glass-chart', label: 'Thorough assessment' },
    { icon: 'fa-solid fa-server', label: 'Production-grade' },
    { icon: 'fa-solid fa-shield-halved', label: 'Standards-first' },
  ],
  pillars: [
    {
      eyebrow: 'Our approach',
      title: 'Proven modules. Individual software.',
      body:
        'Every engagement draws on our own platform — auth, data layers, UI kits, deployment tooling — composed into software shaped for your domain. Faster delivery, true custom fit.',
      cta: { label: 'See how we work', to: '/how-we-work' },
      visual: {
        icon: 'fa-solid fa-layer-group',
        label: 'Compose → customise → ship',
      },
      reversed: false,
    },
    {
      eyebrow: 'Proof of impact',
      title: 'When the application becomes the product',
      body:
        'We have shipped tailored software around real operations that scaled to millions of users — not as a side project, but as the thing the business runs on.',
      highlight: 'Established companies can build new products too. A well-designed application can open a new chapter for the business.',
      cta: { label: 'About the studio', to: '/about' },
      visual: {
        icon: 'fa-solid fa-arrow-trend-up',
        label: 'Impact at scale',
      },
      reversed: true,
    },
  ],
  audience: {
    eyebrow: 'Who we work with',
    title: 'Built for technical teams',
    lead: 'We work best with technical teams looking for a hands-on engineering partner.',
  },
  experienceTeaser: {
    eyebrow: 'Experience NODEMOS',
    title: 'Interactive portals — in development',
    lead:
      'Playable portals are on the roadmap. Here is what we are building for you to explore when they go live.',
    cta: { label: 'View experience', to: '/experience', icon: 'fa-solid fa-arrow-right' },
  },
  capabilities: {
    eyebrow: 'Technical scope',
    title: 'Where we go deep',
    lead: 'The layers we focus on once a project is underway.',
  },
  cta: {
    title: 'Let\'s talk about your project',
    lead: 'Pitch us your idea, explore the NODEMOS experience, or tell us what you need built. A member of the team will get back to you.',
    primary: { label: 'Configure your product', to: '/onboarding', icon: 'fa-solid fa-sitemap' },
    pitch: { label: 'Start a pitch', to: '/pitch', icon: 'fa-solid fa-rocket' },
    secondary: { label: 'Get in touch', to: '/contact', icon: 'fa-solid fa-envelope' },
  },
}

export const audiences = [
  {
    featured: true,
    chip: 'Developers',
    chipIcon: 'fa-solid fa-code',
    title: 'Engineering teams',
    body:
      'API-first design, clear trade-offs, and depth where the UI meets the data model. We work as an extension of your team.',
    cta: 'Our process',
    to: '/how-we-work',
  },
  {
    featured: true,
    chip: 'Product & engineering',
    chipIcon: 'fa-solid fa-sitemap',
    title: 'Developer-friendly companies',
    body:
      'Software that maps to how your business actually operates — built around your workflows, roles, and integrations.',
    cta: 'About us',
    to: '/about',
  },
  {
    featured: false,
    chip: 'New products',
    chipIcon: 'fa-solid fa-bolt',
    title: 'Greenfield inside legacy',
    body:
      'New offerings inside established organisations — products designed to create real value, alongside what you already run.',
    cta: 'Our beliefs',
    to: '/about',
  },
  {
    featured: false,
    chip: 'Healthcare',
    chipIcon: 'fa-solid fa-heart-pulse',
    title: 'Regulated health tech',
    body:
      'A dedicated practice area: FHIR-native interfaces, clinical workflows, and interoperability for EU healthcare contexts.',
    cta: 'Healthcare',
    to: '/healthcare',
  },
  {
    featured: false,
    chip: 'Startups',
    chipIcon: 'fa-solid fa-rocket',
    title: 'Early-stage teams',
    body:
      'Solid architecture from the start, with co-build options when the fit is right for both sides.',
    cta: 'Startup path',
    to: '/startups',
  },
  {
    featured: false,
    chip: 'Handover',
    chipIcon: 'fa-solid fa-book',
    title: 'Your team, equipped',
    body:
      'Clear documentation and knowledge transfer so ownership stays with you after launch.',
    cta: 'Contact',
    to: '/contact',
  },
]

export const capabilities = [
  {
    icon: 'fa-solid fa-cubes',
    title: 'Modular architecture',
    body: 'Bounded contexts your engineers can own and extend independently.',
  },
  {
    icon: 'fa-brands fa-react',
    title: 'React frontends',
    body: 'Interfaces built for long-term change and ongoing iteration.',
  },
  {
    icon: 'fa-solid fa-heart-pulse',
    title: 'FHIR & interoperability',
    body: 'Standards-based data layers where compliance is non-negotiable.',
  },
  {
    icon: 'fa-solid fa-chart-line',
    title: 'Operations UIs',
    body: 'Dashboards and portals over complex, high-volume backends.',
  },
  {
    icon: 'fa-solid fa-cloud',
    title: 'Cloud-native ops',
    body: 'Kubernetes-ready delivery from the first architecture sketch.',
  },
  {
    icon: 'fa-solid fa-diagram-project',
    title: 'Internal tooling',
    body: 'Workflow software tailored to your process and your team.',
  },
]

export const experience = {
  hero: {
    eyebrow: 'Experience NODEMOS',
    title: 'Try our portals',
    lead:
      'Interactive portals are the clearest way to see how we work — compartmentalised, polished, and standards-aware. They are coming soon. Here is the lineup.',
  },
  status: 'Coming soon',
  items: [
    {
      id: 'fhir-portal',
      chip: 'Healthcare',
      chipIcon: 'fa-solid fa-heart-pulse',
      title: 'FHIR Portal',
      body:
        'Interactive Questionnaire renderer, live FHIR resource output, and IG navigation — standards depth with a usable, polished interface.',
      audience: 'Healthtech · Clinical informatics · EU eHealth',
      status: 'planned',
    },
    {
      id: 'modular-playground',
      chip: 'Architecture',
      chipIcon: 'fa-solid fa-cubes',
      title: 'Modular Playground',
      body:
        'Enable, disable, and reconfigure modules to see the system adapt — a live illustration of compartmentalised design.',
      audience: 'Engineering teams · Technical leads',
      status: 'planned',
    },
    {
      id: 'ops-dashboard',
      chip: 'Operations',
      chipIcon: 'fa-solid fa-chart-line',
      title: 'Operations Dashboard',
      body:
        'Reactive filtering, charting, and recursive data views over a high-volume backend — complex operations made manageable.',
      audience: 'SMEs · Product teams · Internal tooling',
      status: 'planned',
    },
  ],
  components: {
    eyebrow: 'Reusable building blocks',
    title: 'Try our Components',
    lead:
      'Drop-in demos of the product pieces we ship into real apps — payments, identity, commerce, documents, support, and maps. Placeholders for now; each will become an interactive, inspectable experience.',
    status: 'Coming soon',
    items: [
      {
        id: 'demo-3d-secure',
        chip: 'Payments',
        chipIcon: 'fa-solid fa-shield-halved',
        title: 'Demo 3D Secure',
        body:
          'Walk through a frictionless and challenge 3-D Secure flow — cardholder authentication, issuer response, and payment outcome without real money moving.',
        audience: 'Checkout · Subscriptions · PCI-aware products',
        status: 'planned',
      },
      {
        id: 'demo-oidc',
        chip: 'Identity',
        chipIcon: 'fa-solid fa-id-badge',
        title: 'Demo OIDC',
        body:
          'OpenID Connect sign-in end to end — authorize, exchange code, inspect ID and access tokens, and map claims into product roles.',
        audience: 'B2B portals · Developer products · Multi-tenant apps',
        status: 'planned',
      },
      {
        id: 'demo-sso',
        chip: 'Identity',
        chipIcon: 'fa-solid fa-key',
        title: 'Demo SSO',
        body:
          'Single sign-on across multiple apps in one session — federate once, land in the right portal with the right role, and sign out cleanly.',
        audience: 'Enterprise · Corporate IdP · Multi-portal products',
        status: 'planned',
      },
      {
        id: 'online-store',
        chip: 'Commerce',
        chipIcon: 'fa-solid fa-store',
        title: 'Online store',
        body:
          'Catalog, cart, checkout, and order confirmation — a complete commerce loop wired to product, order, and payment resources.',
        audience: 'E-commerce · Marketplaces · D2C brands',
        status: 'planned',
      },
      {
        id: 'email-builder',
        chip: 'Communications',
        chipIcon: 'fa-solid fa-envelope-open-text',
        title: 'Email builder demo',
        body:
          'Compose transactional and marketing emails with blocks, variables, and live preview — then see the payload your product would send.',
        audience: 'Lifecycle · Notifications · Customer ops',
        status: 'planned',
      },
      {
        id: 'document-pdf-builder',
        chip: 'Documents',
        chipIcon: 'fa-solid fa-file-pdf',
        title: 'Fast, Secure & Pretty Documents',
        body:
          'Document PDF Builder — templates, branding, and secure generation of contracts, invoices, and reports that look production-ready.',
        audience: 'Legal · Finance · Customer-facing PDFs',
        status: 'planned',
      },
      {
        id: 'create-ticket',
        chip: 'Support',
        chipIcon: 'fa-solid fa-ticket',
        title: 'Create new ticket demo',
        body:
          'Open a support case from a simple form — priority, category, attachments, and a live case record operators can work from.',
        audience: 'Helpdesk · Customer success · Internal IT',
        status: 'planned',
      },
      {
        id: 'google-maps',
        chip: 'Maps',
        chipIcon: 'fa-solid fa-map-location-dot',
        title: 'Google Maps integration',
        body:
          'Places search, geocoding, and map embedding — location pickers and address flows ready to drop into scheduling and logistics UIs.',
        audience: 'Field ops · Logistics · Location-aware products',
        status: 'planned',
      },
    ],
  },
  cta: {
    title: 'Want early access?',
    lead: 'Tell us which portal or component interests you most — we will notify you when it goes live.',
    button: { label: 'Request access', to: '/contact', icon: 'fa-solid fa-bell' },
  },
}

export const pitch = {
  hero: {
    eyebrow: 'Startup path',
    title: "You don't believe the speed we work at?",
    lead: 'Test it today. Submit your pitch — we review every submission personally.',
  },
  criteria: {
    title: 'What we look for',
    items: [
      'Technical innovation that aligns with our strengths — complex UIs, data models, integrations',
      'A founder or team with a clear sense of what they are building and why',
      'Market potential with a path to real users',
      'Alignment for a long-term partnership, not a one-off engagement',
    ],
  },
  process: {
    title: 'How it works',
    steps: [
      {
        icon: 'fa-solid fa-paper-plane',
        title: 'Submit your pitch',
        body: 'Share the idea, your stage, and why NODEMOS. We value substance over slogans.',
      },
      {
        icon: 'fa-solid fa-magnifying-glass',
        title: 'We review',
        body: 'Every pitch is reviewed by the team — not routed through an auto-reply.',
      },
      {
        icon: 'fa-solid fa-compass',
        title: 'Paid discovery',
        body: 'Strong fits begin with a scoped discovery phase — architecture, risks, and an honest assessment.',
      },
      {
        icon: 'fa-solid fa-handshake',
        title: 'Co-build option',
        body: 'For exceptional alignment, we discuss hybrid service and equity arrangements. Selective and transparent.',
      },
    ],
  },
  form: {
    title: 'Pitch form',
    lead: 'Full form integration coming soon. For now, reach out with the details below.',
    fields: [
      { id: 'idea', label: 'Idea summary', placeholder: 'What are you building, and for whom?' },
      { id: 'stage', label: 'Stage', placeholder: 'Concept · MVP · Scaling · Other' },
      { id: 'vision', label: 'Technical vision', placeholder: 'Stack, architecture, integrations, standards' },
      { id: 'why', label: 'Why NODEMOS', placeholder: 'What made you think of us specifically?' },
      { id: 'team', label: 'Team notes', placeholder: 'Who is building this, and what do you bring?' },
    ],
    submitLabel: 'Submit pitch',
    alternateCta: { label: 'Standard contact form', to: '/contact', icon: 'fa-solid fa-envelope' },
  },
}

export const contact = {
  hero: {
    eyebrow: 'Contact',
    title: 'Start a conversation',
    lead: 'Tell us about your product, platform, or engineering requirements. A member of the team will respond personally.',
  },
  aside: {
    eyebrow: 'Startup with a pitch?',
    title: 'Use the pitch form instead',
    body: 'If you are an early-stage team with a product idea and want to explore a co-build, the pitch path is built for that.',
    points: [
      'Substantial pitch for startup and product ideas',
      'Reviewed personally by the team',
      'Selective co-build for the right fit',
    ],
  },
  pitchCta: { label: 'Pitch your idea', to: '/pitch', icon: 'fa-solid fa-rocket' },
  form: {
    title: 'Contact form',
    lead: 'General enquiries, project discussions, and technical questions.',
    fields: [
      { id: 'name', label: 'Name', type: 'text', placeholder: 'Your name', required: true, half: true },
      { id: 'email', label: 'Email', type: 'email', placeholder: 'you@company.com', required: true, half: true },
      { id: 'company', label: 'Company', type: 'text', placeholder: 'Optional', required: false, half: true },
      { id: 'subject', label: 'Subject', type: 'text', placeholder: 'What is this about?', required: false, half: true },
      {
        id: 'message',
        label: 'Message',
        type: 'textarea',
        placeholder: 'Tell us what you are building, your timeline, and how we can help.',
        required: true,
        rows: 6,
      },
    ],
    consentLabel:
      'I confirm that Hartenstein GmbH (NODEMOS) may store my details for 90 days to process this enquiry and serve me as a client. I have read the',
    submitLabel: 'Send message',
    submitEndpoint: '',
    successTitle: 'Thank you',
    successLead: 'Your message is ready to send. If your email client opened, complete the send there — otherwise contact us directly.',
  },
}

export const about = {
  hero: {
    eyebrow: 'About us',
    title: 'A studio for production software',
    lead:
      'NODEMOS is an Austrian product design studio. We partner with technical teams to ship software that holds up in production — and helps companies grow into new products.',
  },
  beliefs: [
    {
      icon: 'fa-solid fa-microchip',
      title: 'Technology creates opportunity',
      body:
        'A well-built system can do more than connect to existing infrastructure. It can become a real advantage for your business.',
    },
    {
      icon: 'fa-solid fa-building-columns',
      title: 'Legacy is a starting point',
      body:
        'Institutional knowledge matters. With the right product, established companies can build something new without leaving their foundations behind.',
    },
    {
      icon: 'fa-solid fa-gem',
      title: 'Craft over shortcuts',
      body:
        'We build software for your context — considered architecture, clean code, and delivery you can maintain and extend.',
    },
    {
      icon: 'fa-solid fa-map-location-dot',
      title: 'Rooted in Austria, built for EU reality',
      body:
        'Regulatory compliance, data residency, and accountable partnership — backed by Austrian engineering discipline.',
    },
  ],
  studio: {
    title: 'What to expect',
    points: [
      'Structured studio delivery with a clear process.',
      'Assessment before commitment — we understand the problem first.',
      'Handover documentation included in every engagement.',
      'React, Node.js, Kubernetes — our production stack.',
    ],
  },
  cta: {
    title: 'Want to see if we\'re a good fit?',
    lead: 'Share your product, team, and constraints. We\'ll have an honest conversation about how we can help.',
    button: { label: 'Contact the team', to: '/contact', icon: 'fa-solid fa-envelope' },
  },
}

export const howWeWork = {
  hero: {
    eyebrow: 'How we work',
    title: 'Your software, accelerated by ours',
    lead:
      'Projects begin with modules we already run in production — then diverge into software shaped entirely for your requirements.',
  },
  model: {
    title: 'Compose, then customise',
    steps: [
      {
        icon: 'fa-solid fa-puzzle-piece',
        title: 'Shared foundation',
        body: 'Secure sign-in, identity provision, payment flows, data access patterns, UI primitives, and deployment — maintained across our own products and available from your first pilot.',
      },
      {
        icon: 'fa-solid fa-sliders',
        title: 'Domain shaping',
        body: 'Selected modules extended and recomposed around your workflows, roles, and integration landscape.',
      },
      {
        icon: 'fa-solid fa-rocket',
        title: 'Focused velocity',
        body: 'Engineering time spent on what differentiates you — not reinventing solved problems.',
      },
    ],
  },
  foundations: {
    eyebrow: 'Platform foundations',
    title: 'Built in from your first pilot',
    lead:
      'Secure identity and payment flows are part of how we ship — not integrations you bolt on after launch. Every portal gets the access and verification layer it needs; revenue flows are shaped when your business model requires them.',
    badge: 'From first pilot',
    modules: [
      {
        label: 'Secure sign-in & identity',
        description:
          'Identity provision and identity checking for your customers — who can sign in to each portal, how they are verified, and how access is managed across the product.',
        detail:
          'Role-aware access per portal and verification flows from the first pilot onward. Users sign in with Google, Apple, or GitHub — or you connect your own identity provider (OIDC/SAML) when you already run one.',
        signInProviders: [
          { label: 'Google', icon: 'fa-brands fa-google' },
          { label: 'Apple', icon: 'fa-brands fa-apple' },
          { label: 'GitHub', icon: 'fa-brands fa-github' },
          { label: 'External Identity Provider', icon: 'fa-solid fa-key' },
        ],
        icon: 'fa-solid fa-shield-halved',
        sketch: 'secure_identity',
        infoLink: {
          label: 'Identity provider integration',
          to: '/integrations/identity-provider',
        },
      },
      {
        label: 'Payment flows',
        description:
          'When revenue is part of the journey, payment handling is composed into your product — subscriptions, one-off charges, invoicing, or marketplace settlements.',
        detail:
          'Configured to your pricing model and process, not a generic checkout widget added at the end. Available from the first pilot when payments matter to your product.',
        icon: 'fa-solid fa-credit-card',
        sketch: 'payments',
        infoLink: {
          label: 'Payment provider integration',
          to: '/integrations/payment-provider',
        },
      },
    ],
    note:
      'In the product configuration flow you tell us which foundations your portals need — we map them into architecture and your first pilot scope.',
    cta: { label: 'Configure your product', to: '/onboarding', icon: 'fa-solid fa-sitemap' },
  },
  process: {
    title: 'Engagement flow',
    lead:
      'A structured path from first conversation to software your team owns — with assessment before commitment at every step.',
    commitment: {
      eyebrow: 'Your investment',
      title: 'We respect your time before we ask for your trust',
      lead:
        'Every phase has a clear purpose, defined outputs, and an honest conversation about fit. No lengthy sales cycles, no vague scopes, no surprise handovers.',
      points: [
        {
          icon: 'fa-solid fa-clipboard-check',
          title: 'Assessment before commitment',
          body: 'We understand your context and constraints before proposing work — and we will tell you plainly if we are not the right partner.',
        },
        {
          icon: 'fa-solid fa-receipt',
          title: 'Scoped phases, clear deliverables',
          body: 'Each step produces something tangible you can evaluate. Discovery and architecture are paid, focused engagements — not open-ended consulting.',
        },
        {
          icon: 'fa-solid fa-user-gear',
          title: 'Engineers in the room',
          body: 'You work directly with the people who design and build — not account managers relaying messages.',
        },
        {
          icon: 'fa-solid fa-book-open',
          title: 'Ownership from day one',
          body: 'Documentation and knowledge transfer are part of delivery, not an afterthought bolted on at the end.',
        },
      ],
    },
    steps: [
      {
        slug: 'assessment',
        icon: 'fa-solid fa-magnifying-glass',
        label: 'Assessment',
        detail: 'Technical and operational review before any proposal.',
        summary:
          'We map your systems, constraints, and goals — then give you an honest read on fit before anyone commits to a build.',
        hero: {
          title: 'Assessment',
          lead:
            'A focused technical review of your context, risks, and requirements — so the next step is an informed decision, not a sales pitch.',
        },
        overview:
          'Assessment is where we earn the right to propose anything. We spend time with your engineers and stakeholders to understand what you run today, what you need to change, and whether NODEMOS is genuinely the right partner for it.',
        activities: [
          {
            title: 'Context mapping',
            body: 'Existing systems, integrations, team structure, and regulatory or operational constraints that shape what is possible.',
          },
          {
            title: 'Technical depth review',
            body: 'Architecture sketches, data models, and integration points — reviewed with the people who will maintain what ships.',
          },
          {
            title: 'Risk and fit assessment',
            body: 'Honest evaluation of complexity, timeline realism, and alignment with our strengths. We decline projects that are not a good match.',
          },
          {
            title: 'Path recommendation',
            body: 'A clear recommendation: proceed to architecture, adjust scope, pause, or part ways — with reasoning you can act on.',
          },
        ],
        deliverables: [
          'Written assessment summary with findings and risks',
          'Recommended engagement path (or a clear explanation why not)',
          'Rough effort and timeline ranges for the next phase',
        ],
        investment: {
          title: 'How we protect your time',
          points: [
            'Short, focused sessions with technical stakeholders — not weeks of stakeholder workshops',
            'No proposal without understanding; no pressure to move forward if the fit is wrong',
            'You receive a concrete summary you can use internally, whether you work with us or not',
          ],
        },
        timeline: 'Typically 1–2 weeks, depending on system complexity and access to stakeholders.',
      },
      {
        slug: 'architecture',
        icon: 'fa-solid fa-drafting-compass',
        label: 'Architecture',
        detail: 'System boundaries, scale path, and module map.',
        summary:
          'Decisions documented, boundaries drawn, and a module map that shows what we compose from our platform and what we build for your domain.',
        hero: {
          title: 'Architecture',
          lead:
            'System boundaries, integration contracts, and a module composition plan — so build time goes into what differentiates you.',
        },
        overview:
          'Architecture turns assessment findings into a buildable plan. We define bounded contexts, choose where our production modules apply, and document the decisions your team will need long after we ship.',
        activities: [
          {
            title: 'Boundary definition',
            body: 'Clear module boundaries so teams can work in parallel without stepping on each other\'s changes.',
          },
          {
            title: 'Platform composition',
            body: 'Mapping which NODEMOS foundations and modules — secure identity, payments, data layers, UI kits, deployment — apply from your first pilot and where domain-specific work is needed.',
          },
          {
            title: 'Integration design',
            body: 'API contracts, event flows, and standards choices (FHIR, OpenAPI, OIDC) applied where they genuinely help.',
          },
          {
            title: 'Scale and operations path',
            body: 'How the system grows from first release to production load — including observability, deployment, and runbook foundations.',
          },
        ],
        deliverables: [
          'Architecture overview with system context diagram',
          'Module map: shared foundation vs domain-specific components',
          'Milestone plan with release boundaries',
          'Key decision records for major technical choices',
        ],
        investment: {
          title: 'How we protect your time',
          points: [
            'Decisions documented so your team is not re-explaining context in every meeting',
            'Spikes only where uncertainty is high — not exploratory work disguised as architecture',
            'Architecture you can hand to your engineers and extend independently',
          ],
        },
        timeline: 'Typically 2–4 weeks for a well-scoped product or platform area.',
      },
      {
        slug: 'build',
        icon: 'fa-solid fa-hammer',
        label: 'Build',
        detail: 'Iterative releases of production-ready components.',
        summary:
          'Iterative releases built on our production modules — with your engineers in the loop and scope managed in visible milestones.',
        hero: {
          title: 'Build',
          lead:
            'Production-ready software in iterative releases — engineering effort focused on your domain, not reinventing solved problems.',
        },
        overview:
          'Build is where architecture becomes running software. We ship in milestones you can evaluate, integrate with your team\'s workflows, and keep you close to decisions that affect the product.',
        activities: [
          {
            title: 'Milestone delivery',
            body: 'Working releases at agreed boundaries — each producing something deployable or demonstrable, not slide decks.',
          },
          {
            title: 'Parallel engineering',
            body: 'Your engineers and ours on shared repositories, code review, and architecture checkpoints — not a black box.',
          },
          {
            title: 'Module extension',
            body: 'Composing and extending NODEMOS platform modules around your workflows, roles, and data model.',
          },
          {
            title: 'Production hardening',
            body: 'CI/CD pipelines, test coverage on critical paths, and operational readiness as part of each release — not a final scramble.',
          },
        ],
        deliverables: [
          'Deployable application components per milestone',
          'CI/CD configuration and environment setup',
          'Test coverage on business-critical paths',
          'Running changelog and release notes for your team',
        ],
        investment: {
          title: 'How we protect your time',
          points: [
            'Shared foundation means velocity — identity, payments, deployment, and UI primitives already solved from the first pilot',
            'Scope managed in milestones with visible progress; surprises surfaced early',
            'No vanity features: build effort goes to what differentiates your product',
          ],
        },
        timeline: 'Varies by scope; milestones are typically 2–6 weeks each with regular checkpoints.',
      },
      {
        slug: 'handover',
        icon: 'fa-solid fa-book-open',
        label: 'Handover',
        detail: 'Documentation and transfer so your team owns what ships.',
        summary:
          'Documentation, knowledge transfer, and operational guides — built throughout the project, not rushed at the end.',
        hero: {
          title: 'Handover',
          lead:
            'Your team equipped to own, extend, and operate what we built together — with documentation that matches the software.',
        },
        overview:
          'Handover is not a single meeting at the end. Documentation, runbooks, and knowledge transfer run alongside build so ownership transfers cleanly when the engagement concludes.',
        activities: [
          {
            title: 'Living documentation',
            body: 'Architecture docs, API references, and module guides updated as the system evolves — not written from memory at the finish line.',
          },
          {
            title: 'Knowledge transfer sessions',
            body: 'Structured walkthroughs with your engineers covering design decisions, operational procedures, and extension points.',
          },
          {
            title: 'Operational readiness',
            body: 'Runbooks, deployment guides, and monitoring setup so your team can run production without us in the loop.',
          },
          {
            title: 'Ownership transition',
            body: 'Repository access, credential handover, and a clear support boundary — what we cover post-launch and what your team owns.',
          },
        ],
        deliverables: [
          'Architecture and module documentation package',
          'API and integration reference',
          'Operational runbooks and deployment guides',
          'Recorded or written knowledge transfer materials',
        ],
        investment: {
          title: 'How we protect your time',
          points: [
            'Handover built in from the first milestone — no scramble or knowledge locked in our heads',
            'Documentation aimed at your engineers, not marketing copy',
            'You leave with software you can maintain without a permanent dependency on us',
          ],
        },
        timeline: 'Ongoing throughout build; formal transfer typically 1–2 weeks at project conclusion.',
      },
    ],
  },
  principles: [
    {
      icon: 'fa-solid fa-cubes',
      title: 'Compartmentalization',
      body: 'Clear module boundaries so teams can work in parallel with fewer conflicts.',
    },
    {
      icon: 'fa-solid fa-link',
      title: 'Standards on merit',
      body: 'FHIR, OpenAPI, OIDC — we use them when they genuinely help the project.',
    },
    {
      icon: 'fa-solid fa-users-gear',
      title: 'Operator-first UX',
      body: 'Interfaces designed around the people who use them every day.',
    },
  ],
  stack: {
    label: 'Core stack',
    value: 'React · Node.js · Kubernetes',
    note: 'In production across our own products',
  },
  cta: {
    title: 'Planning a build or a rebuild?',
    lead: 'Whether you are replacing legacy infrastructure or launching something new — we\'d like to hear from you.',
    button: { label: 'Discuss your project', to: '/contact', icon: 'fa-solid fa-comments' },
  },
}

export const header = {
  links: [
    { label: 'About', to: '/about' },
    { label: 'How we work', to: '/how-we-work' },
    { label: 'Experience', to: '/experience' },
  ],
  actions: [
    { label: 'Configure', to: '/onboarding', icon: 'fa-solid fa-sitemap', variant: 'ghost' },
    { label: 'Contact', to: '/contact', icon: 'fa-solid fa-envelope', variant: 'primary' },
  ],
}

export const pages = {
  healthcare: {
    eyebrow: 'Healthcare',
    title: 'Healthcare & FHIR',
    lead: 'Interoperability-native UIs, clinical workflows, and standards-based applications — one specialised branch of our work.',
    primaryCta: { label: 'Discuss a project', to: '/contact', icon: 'fa-solid fa-comments' },
  },
  startups: {
    eyebrow: 'Startups',
    title: 'Architecture-first foundations',
    lead: 'For teams ready to scale — with selective co-build arrangements when the fit is right for both sides.',
    primaryCta: { label: 'Pitch your idea', to: '/pitch', icon: 'fa-solid fa-rocket' },
    secondaryCta: { label: 'How we work', to: '/how-we-work', icon: 'fa-solid fa-compass' },
  },
}

export const footerLinks = [
  { label: 'Configure product', to: '/onboarding' },
  { label: 'Experience', to: '/experience' },
  { label: 'Pitch your idea', to: '/pitch' },
  { label: 'About', to: '/about' },
  { label: 'How we work', to: '/how-we-work' },
  { label: 'Healthcare', to: '/healthcare' },
  { label: 'Contact', to: '/contact' },
]